import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Text,
  keyframes,
  useToast,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import OtpInput from 'react-otp-input';
import * as authenticationAPI from '@/api/authentication';
import * as baseAPI from '@/api/base';
import { storage } from '@/utils/storage';
import Logo from '../logo';
import { MdAutorenew } from 'react-icons/md';
import { useInterval } from '@/hooks/use-interval';
import { debounce } from '@/utils/debounce';
import { useLoading } from '@/hooks/use-loading';
import goftino from '@/utils/goftino';

const OTP_LENGTH = 6;
const OTP_TIMER = 2 * 60;

const rotateAnimationKeyframes = keyframes`
  0% { transform: rotate(0); }
  100% { transform: rotate(180deg); }
`;
const rotateAnimation = `${rotateAnimationKeyframes} 0.8s ease infinite`;

type Props = {
  onSuccess: VoidFunction;
  sendOtp: (mobileNumber: string) => Promise<void>;
  onResendClick: VoidFunction;
};
export default function Otp({ onSuccess, sendOtp, onResendClick }: Props) {
  const toast = useToast();

  const [timer, setTimer] = useState(OTP_TIMER);
  const { stop } = useInterval(() => {
    setTimer((p) => {
      const v = --p;
      if (v === 0) {
        stop();
      }
      return v;
    });
  }, 1000);

  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useLoading({
    verify: false,
    resend: false,
  });

  const verifyOtp = useMemo(
    () =>
      debounce(async (otp: string) => {
        try {
          setLoading({ verify: true });
          const result = await authenticationAPI.login({
            method: 'otp',
            mobileNumber: storage.session.mobile.get(),
            otp,
          });
          baseAPI.setAuth(result.body.token);
          storage.local.user.set(result.body.user);
          const address = result.body.user.addresses[0];
          const phone = result.body.user.mobileNumber!;
          goftino.setUser({
            name: address?.name || phone,
            phone,
          });
          await onSuccess();
        } catch (e: any) {
          const errors = {
            429: 'چند دقیقهٔ دیگر دوباره تلاش کنید.',
            403: 'کد وارد شده اشتباه است. دوباره تلاش کنید.',
            default: 'مشکلی پیش آمده است. دوباره تلاش کنید.',
          };
          toast({
            title: errors[e.status as keyof typeof errors] ?? errors.default,
            status: 'error',
          });
        } finally {
          setLoading({ verify: false });
        }
      }),
    [],
  );

  function handleOtpChange(value: string) {
    setOtp(value);
    if (value.length === OTP_LENGTH) {
      verifyOtp(value);
    }
  }

  async function resendOtp() {
    setLoading({ resend: true });
    setOtp('');
    await sendOtp(storage.session.mobile.get());
    setTimer(OTP_TIMER);
    setLoading({ resend: false });
  }

  const isTimerUp = timer === 0;
  const disabled = loading.verify || loading.resend;

  return (
    <Stack px={4} py={6} align={'center'} bgColor={'white'} rounded={'xl'}>
      <Logo mx="auto" />
      <Text fontSize={'sm'} fontWeight={600} align={'center'} mt={4}>
        ورود به حساب کاربری
      </Text>
      <Text fontSize={'xs'} align={'center'} mb={5}>
        شماره همراه خود را وارد کنید
      </Text>
      <OtpInput
        value={otp}
        onChange={handleOtpChange}
        numInputs={OTP_LENGTH}
        renderInput={(props) => (
          <input
            {...props}
            readOnly={disabled}
            style={{ ...props.style, opacity: disabled ? 0.3 : 1 }}
          />
        )}
        containerStyle={{ justifyContent: 'center', direction: 'ltr' }}
        inputStyle={{
          width: 36,
          height: 36,
          borderRadius: 8,
          backgroundColor: '#0000000F',
          margin: '0 4px',
          opacity: disabled ? 0.7 : 1,
          outlineColor: 'black',
          outlineWidth: disabled ? 0 : 2,
        }}
        inputType="number"
      />
      <Button
        onClick={onResendClick}
        variant={'outline'}
        maxW={'170px'}
        mt={4}
        mb={10}
        isDisabled={disabled}
      >
        ویرایش شماره همراه
      </Button>

      {!isTimerUp ? (
        <Stack direction={'row'} align={'center'} fontSize={'sm'}>
          <Counter value={timer} />
          <span>تا ارسال مجدد کد</span>
        </Stack>
      ) : (
        <Button
          variant={'ghost'}
          fontSize={'sm'}
          onClick={resendOtp}
          isDisabled={loading.resend}
        >
          <Box
            as={motion.div}
            animation={loading.resend ? rotateAnimation : undefined}
          >
            <MdAutorenew size={20} />
          </Box>
          <Box as="span" mr={2}>
            ارسال مجدد کد تایید
          </Box>
        </Button>
      )}
    </Stack>
  );
}

function Counter({ value }: { value: number }) {
  const m = Math.floor(value / 60)
    .toString()
    .padStart(2, '0');
  const s = (value % 60).toString().padStart(2, '0');
  return (
    <Stack direction={'row'} align={'center'} spacing={'2px'} minW={'40px'}>
      <span>{s}</span>
      <span>:</span>
      <span>{m}</span>
    </Stack>
  );
}
